import { fromTable } from '@/integrations/supabase/db';
import { SearchResult, SearchSectionId } from '../types';
import { getMatchFunction, getServerPattern, getServerLimit, calculateRelevanceScore } from '../utils';

export async function searchRabatte(
  term: string, 
  limit: number, 
  exactMatch: boolean,
  fuzzyMatch: boolean = false,
  signal?: AbortSignal
): Promise<SearchResult[]> {
  if (!term.trim()) return [];
  
  const pattern = getServerPattern(term, fuzzyMatch);
  const serverLimit = getServerLimit(limit, fuzzyMatch);
  const matchFn = getMatchFunction(exactMatch, fuzzyMatch);
  
  // Step 1: Find matching Gesellschaften
  const { data: matchingGesellschaften } = await fromTable('gesellschaften')
    .select('id, name')
    .ilike('name', pattern)
    .limit(fuzzyMatch ? 100 : 50);
  
  const matchingGesellschaftIds = (matchingGesellschaften || []).map(g => g.id);
  const gesellschaftById = new Map((matchingGesellschaften || []).map(g => [g.id, g.name]));
  
  // Step 2: Query Rabatte
  const orConditions = [
    `sparte.ilike.${pattern}`,
    `rabatt.ilike.${pattern}`,
    `produkt.ilike.${pattern}`,
    `tarif.ilike.${pattern}`
  ];
  
  if (matchingGesellschaftIds.length > 0) {
    orConditions.push(`gesellschaft_id.in.(${matchingGesellschaftIds.join(',')})`);
  }
  
  const { data, error } = await fromTable('rabatte_sideletter')
    .select('id, sparte, rabatt, rahmenvereinbarung, produkt, tarif, kategorie, gesellschaft_id, updated_at')
    .or(orConditions.join(','))
    .limit(serverLimit);

  if (error) {
    console.error('Rabatte search error:', error);
    return [];
  }

  // Step 3: Load missing Gesellschaften
  const allGesellschaftIds = [...new Set((data || []).map(r => r.gesellschaft_id).filter(Boolean))];
  const missingGesellschaftIds = allGesellschaftIds.filter(id => !gesellschaftById.has(id));
  
  if (missingGesellschaftIds.length > 0) {
    const { data: additionalGes } = await fromTable('gesellschaften')
      .select('id, name')
      .in('id', missingGesellschaftIds);
    (additionalGes || []).forEach(g => gesellschaftById.set(g.id, g.name));
  }

  // Step 4: Multi-term filter with all fields
  return (data || [])
    .filter(r => {
      const gesellschaftName = gesellschaftById.get(r.gesellschaft_id) || '';
      return matchFn(term, 
        r.sparte, r.rabatt, r.produkt, r.tarif, r.kategorie, gesellschaftName
      );
    })
    .slice(0, limit)
    .map(r => {
      const title = r.produkt || r.tarif || r.sparte || 'Rabatt';
      const subtitle = [gesellschaftById.get(r.gesellschaft_id), r.rabatt ? `${r.rabatt}` : null].filter(Boolean).join(' • ');
      
      return {
        id: r.id,
        type: 'rabatte' as SearchSectionId,
        title,
        subtitle,
        badge: r.kategorie || 'Rabatt',
        data: r,
        relevanceScore: calculateRelevanceScore(term, title, subtitle, r.updated_at)
      };
    });
}
